// src/components/mdx/shared/footnote-item.ts

/**
 * @fileoverview Single source of truth for footnote-item DOM markup.
 * @description Pure, framework-agnostic string builders for the per-item markup
 * of the footnotes/references list. This module is imported by BOTH rendering
 * paths so the two can never drift again:
 *
 * 1. SERVER — `FootnoteList` (`../server/Footnotes.ssr.tsx`) emits the item
 *    region via `dangerouslySetInnerHTML` from {@link renderFootnoteItemHTML}.
 * 2. CLIENT — `ClientFootnotesWrapper` (`../wrappers/ClientFootnotesWrapper.client.tsx`)
 *    wipes-and-rebuilds the live list from the same builder after scanning the DOM.
 *
 * WHY A SHARED STRING BUILDER (not shared JSX): the client path rebuilds the
 * list imperatively via `element.innerHTML` — it re-scans `sup[data-footnote-id]`
 * to recover footnotes emitted by client-only MDX components that the server
 * `cache()` collector never saw, so the wipe-and-rebuild is LOAD-BEARING, not
 * accidental. A shared HTML string is the only form both a React server render
 * and an imperative `innerHTML` rebuild can consume verbatim, guaranteeing the
 * markup is byte-identical by construction rather than by hand-synced copies.
 *
 * This module has NO `"use client"` directive and NO React import: it is a pure
 * value module usable from a Server Component and a Client Component alike.
 *
 * @module src/components/mdx/shared/footnote-item
 */

/**
 * Footnote source type indicating the kind of reference material, used to pick
 * the source icon. The runtime value on the client is read from a `data-*`
 * attribute so it is widened to `string` at the builder boundary.
 */
export type FootnoteSourceType = 'book' | 'article' | 'paper' | 'website' | 'other'

/**
 * Flat footnote-item data consumed by the shared markup builders. The server's
 * nested `FootnoteData.source` is flattened into this shape at its call site;
 * the client already collects this flat shape from the DOM.
 */
export interface FootnoteItem {
  /** Unique footnote identifier (typically numeric, e.g. "1"). */
  id: string
  /** Full citation text for the source. */
  citation: string
  /**
   * Source type used to select the icon. Accepts the canonical
   * {@link FootnoteSourceType} literals (which drive editor autocomplete) but is
   * widened to accept any `string`, because the client path reads this value
   * from a `data-*` attribute and cannot statically guarantee a canonical
   * member. `string & {}` keeps the literal suggestions while still admitting an
   * arbitrary DOM-sourced string, without the literals being collapsed into
   * `string` (which would erase the autocomplete and is what
   * `no-redundant-type-constituents` flags on a bare `... | string`).
   */
  type: FootnoteSourceType | (string & {})
  /** Optional external URL (renders an "open in new tab" affordance when present). */
  url?: string
}

/**
 * SVG `<path>` `d` data for each source-icon, keyed by canonical type. These are
 * the exact Heroicons (`@heroicons/react/24/outline`) path strings the server
 * previously rendered via the icon components — extracted verbatim so the icon
 * is pixel-identical to before, now defined in ONE place:
 * - `book`    → `BookOpenIcon`
 * - `article` / `paper` → `NewspaperIcon`
 * - `website` → `GlobeAltIcon`
 * - `other` / default   → `DocumentTextIcon`
 */
const SOURCE_ICON_PATHS: Record<'book' | 'newspaper' | 'website' | 'document', string> = {
  book: 'M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25',
  newspaper:
    'M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z',
  website:
    'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418',
  document:
    'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
}

/** Heroicons `ArrowTopRightOnSquareIcon` `d` — the external-link affordance. */
const EXTERNAL_LINK_ICON_PATH =
  'M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25'

/**
 * URL scheme allowlist for a footnote's external-link `href`. A footnote `url`
 * is an author-supplied source citation rendered as a `target="_blank"` anchor,
 * so only navigable web schemes are permitted: `http:`, `https:`, `mailto:`,
 * plus root-relative / relative / hash / query paths. Anything else — notably
 * `javascript:` (and `data:` / `vbscript:` / protocol-relative `//host`) — is
 * rejected so the raw-HTML interpolation can never emit an executable or
 * open-redirect link. This restores the safety the React server renderer's
 * href sanitizer gave the prior `<a href={url}>` JSX path, which the shared
 * string builder bypasses (HTML-escaping guards attribute breakout, NOT unsafe
 * protocols).
 *
 * @param rawUrl - The author-supplied footnote URL.
 * @returns `true` when the URL's scheme is safe to place in an `href`.
 */
function isAllowedFootnoteUrl(rawUrl: string): boolean {
  // Normalize the way a browser does before resolving a URL: strip C0 control
  // chars + DEL (so `java\nscript:` can't smuggle a scheme past the check) and
  // canonicalize backslashes to forward slashes (so `\\evil.com` can't resolve
  // to a protocol-relative `//evil.com`).
  const url = rawUrl
    // eslint-disable-next-line no-control-regex -- deliberately stripping C0 controls + DEL
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\\/g, '/')
    .trim()
  // Protocol-relative (`//host`) resolves to an external origin — reject before
  // the root-relative (`/…`) allowance below would let it through.
  if (url.startsWith('//')) return false
  if (/^(https?:|mailto:)/i.test(url)) return true
  if (/^(\/|\.\/|\.\.\/|#|\?)/.test(url)) return true
  // Anything carrying a scheme we don't recognise (incl. `javascript:`) is unsafe.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return false
  // Schemeless, non-path text (e.g. "example.com") — treat as a relative path.
  return true
}

/**
 * Predicate for whether a footnote item should be rendered at all. Empty or
 * whitespace-only `id`/`citation` items are dropped. This is the SINGLE source
 * for the include/exclude decision so the server `FootnoteList` and the client
 * wipe-and-rebuild filter identically — a footnote with a blank citation can no
 * longer render server/no-JS but vanish after hydration (or vice-versa).
 *
 * @param footnote - The footnote item data.
 * @returns `true` when the item has both a non-blank id and citation.
 */
export function isRenderableFootnoteItem(footnote: FootnoteItem): boolean {
  return Boolean(footnote.id?.trim() && footnote.citation?.trim())
}

/**
 * Escapes a string for safe interpolation into HTML text or a double-quoted
 * attribute value. Mirrors React's text/attribute escaping (`& < > " '`) so the
 * server output matches its prior React-escaped form and the client rebuild —
 * which previously interpolated raw — is no longer an innerHTML injection seam.
 *
 * @param value - Untrusted-shaped string (author-supplied citation / URL).
 * @returns The HTML-escaped string.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

/**
 * Maps a footnote source type to its icon `d` path data. `article` and `paper`
 * share the newspaper glyph; unknown/`other` types fall back to the document
 * glyph — matching the server's historical `getSourceIcon` semantics.
 *
 * @param type - The source type (may be a DOM-sourced arbitrary string).
 * @returns The SVG `<path>` `d` data for the icon.
 */
function iconPathForType(type: string): string {
  switch (type) {
    case 'book':
      return SOURCE_ICON_PATHS.book
    case 'article':
    case 'paper':
      return SOURCE_ICON_PATHS.newspaper
    case 'website':
      return SOURCE_ICON_PATHS.website
    default:
      return SOURCE_ICON_PATHS.document
  }
}

/**
 * Renders the source-icon `<svg>` element for a footnote type. The SVG
 * attributes (viewBox, stroke, sizing classes) reproduce the Heroicons outline
 * icon the server rendered, so the icon is visually identical.
 *
 * The icon is DECORATIVE — it sits beside the citation text, which already
 * conveys the source — so it carries `aria-hidden="true"` and NO `role`/
 * `aria-label`. (The prior markup set `aria-hidden="true"` alongside
 * `role="img"` + `aria-label`, which is contradictory: `aria-hidden` makes the
 * role/label inert, so assistive tech would have announced nothing anyway.
 * Dropping them removes the contradiction without changing pixels or the
 * a11y-tree outcome.)
 *
 * @param type - The source type.
 * @returns An `<svg>…</svg>` HTML string.
 */
export function renderFootnoteSourceIconHTML(type: string): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="mt-0.5 h-4 w-4 flex-none text-gray-400">` +
    `<path stroke-linecap="round" stroke-linejoin="round" d="${iconPathForType(type)}" />` +
    `</svg>`
  )
}

/**
 * Renders the full footnote list-item element as an HTML string: the numbered
 * backlink anchor, the source icon, the citation text, and — when a `url` is
 * present — an external-link anchor that opens in a new tab. This is the single
 * source for the per-item DOM shared by the server render and the client rebuild.
 *
 * @param footnote - The footnote item data.
 * @returns A list item containing the semantic footnote and its source links.
 */
export function renderFootnoteItemHTML(footnote: FootnoteItem): string {
  const id = escapeHtml(footnote.id)
  const citation = escapeHtml(footnote.citation)
  // Only emit the external-link anchor when the URL is present AND its scheme is
  // allowlisted; a disallowed scheme (e.g. `javascript:`) drops the link so the
  // citation renders as plain text rather than an unsafe/executable href. Safe
  // URLs render byte-identically to before (escaped, unchanged), preserving the
  // pixel-identical result for normal https footnotes.
  const externalLink =
    footnote.url && isAllowedFootnoteUrl(footnote.url)
    ? `<a href="${escapeHtml(footnote.url)}" target="_blank" rel="noopener noreferrer" class="ml-2 inline-flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300" aria-label="Visit source for footnote ${id} (opens in new tab)">` +
      `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="h-3 w-3">` +
      `<path stroke-linecap="round" stroke-linejoin="round" d="${EXTERNAL_LINK_ICON_PATH}" />` +
      `</svg>` +
      `<span class="sr-only">Visit source</span>` +
      `</a>`
    : ''

  return (
    // doc-footnote is not a listitem role; retain both semantics in the shared
    // server/client markup so the surrounding source list has valid children.
    `<div role="listitem">` +
    `<div id="footnote-${id}" class="group flex gap-3 text-sm footnote-item" role="doc-footnote" tabindex="0" data-footnote-id="${id}">` +
    `<div class="flex-none">` +
    `<a href="#footnote-ref-${id}" class="inline-flex h-5 w-5 items-center justify-center rounded bg-gray-100 text-xs font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white" aria-label="Return to footnote reference ${id}" role="doc-backlink" tabindex="0" title="Return to reference ${id} in the text">${id}</a>` +
    `</div>` +
    `<div class="flex flex-1 items-start gap-2">` +
    renderFootnoteSourceIconHTML(footnote.type) +
    `<div class="flex-1 text-gray-600 dark:text-gray-400" aria-describedby="footnote-ref-${id}">${citation}${externalLink}</div>` +
    `</div>` +
    `</div></div>`
  )
}
